import { INTENT_SIDE_EFFECT_HEADER, SOCKET_PATH } from '@dopt/block-api-types';
import { io } from 'socket.io-client';

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};

function __awaiter(thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
}

function __generator(thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (_) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
}

function getDefaultBlockState(uid, sid, version) {
    return {
        kind: 'block',
        type: 'model',
        uid: uid,
        sid: sid,
        version: version,
        state: {
            active: false,
            entered: false,
            exited: false
        },
        transitioned: {},
        fields: []
    };
}
function getDefaultFlowState(sid, version) {
    return {
        kind: 'flow',
        type: 'flow',
        uid: sid,
        sid: sid,
        version: version,
        state: {
            started: false,
            stopped: false,
            finished: false
        },
        blocks: []
    };
}

/**
 * Hypertext Transfer Protocol (HTTP) response status codes.
 * @see {@link https://developer.mozilla.org/docs/Web/HTTP/Status}
 */
var HttpStatusCodeRanges = {
    InformationalResponses: function (status) {
        return status >= 100 && status < 200;
    },
    SuccessfulResponses: function (status) {
        return status >= 200 && status < 300;
    },
    RedirectionResponses: function (status) {
        return status >= 300 && status < 400;
    },
    ClientErrorResponses: function (status) {
        return status >= 400 && status < 500;
    },
    ServerErrorResponses: function (status) {
        return status >= 500 && status < 600;
    }
};

function errorHandler(response, log) {
    return __awaiter(this, void 0, void 0, function () {
        var error, errorTemplate;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, response.clone().json()];
                case 1:
                    error = _a.sent();
                    errorTemplate = "".concat((error.code || '').toUpperCase(), " : ").concat(error.details);
                    switch (true) {
                        case HttpStatusCodeRanges.ClientErrorResponses(response.status):
                            log.error(errorTemplate);
                            break;
                        case HttpStatusCodeRanges.ServerErrorResponses(response.status):
                            log.warn(errorTemplate);
                            break;
                        default:
                            log.log(response.text());
                    }
                    return [2 /*return*/];
            }
        });
    });
}

function hasSideEffects(response) {
    return response.headers.get(INTENT_SIDE_EFFECT_HEADER) === 'true';
}
var INTENT_POST_OPTIONS = {
    method: 'POST',
    body: '{}',
    headers: {
        'Content-Type': 'application/json'
    }
};
function client(_a) {
    var url = _a.url, apiKey = _a.apiKey, options = _a.options, logger = _a.logger, urlPrefix = _a.urlPrefix, packageVersion = _a.packageVersion, packageName = _a.packageName;
    return __awaiter(this, void 0, void 0, function () {
        var response;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0: return [4 /*yield*/, fetch("".concat(urlPrefix).concat(url), __assign(__assign({}, options), { headers: __assign(__assign({}, options === null || options === void 0 ? void 0 : options.headers), { 'x-api-key': apiKey, 'x-pkg-version': packageVersion, 'x-pkg-name': packageName }) }))];
                case 1:
                    response = _b.sent();
                    if (!response.ok) {
                        errorHandler(response, logger);
                        return [2 /*return*/];
                    }
                    if (response.status === 204) {
                        return [2 /*return*/, response];
                    }
                    return [2 /*return*/, response.json()];
            }
        });
    });
}
var queryParams = function (_a) {
    var userId = _a.userId, groupId = _a.groupId;
    return function (_a) {
        var version = _a.version;
        return "version=".concat(version, "&userIdentifier=").concat(userId).concat(groupId ? "&groupIdentifier=".concat(groupId) : "");
    };
};
function blocksApi(_a) {
    var apiKey = _a.apiKey, userId = _a.userId, logger = _a.logger, config = _a.config, groupId = _a.groupId;
    var query = queryParams({ userId: userId, groupId: groupId });
    return {
        getFlow: function (_a) {
            var sid = _a.sid, version = _a.version;
            return __awaiter(this, void 0, void 0, function () {
                var flow;
                return __generator(this, function (_b) {
                    switch (_b.label) {
                        case 0: return [4 /*yield*/, client(__assign({ url: "/v2/flow/".concat(sid, "?include=block&").concat(query({
                                    version: version
                                })), apiKey: apiKey, logger: logger }, config))];
                        case 1:
                            flow = (_b.sent());
                            if (flow) {
                                logger.info("Flow<{\"sid\":\"".concat(sid, "\",\"version\":").concat(version, "}> fetched successfully."));
                                logger.debug("\n".concat(JSON.stringify(flow, null, 2)));
                            }
                            else {
                                logger.error("An error occurred while fetching Flow<{\"sid\":\"".concat(sid, "\",\"version\":").concat(version, "}>, setting flow state to its defaults."));
                            }
                            return [2 /*return*/, flow || getDefaultFlowState(sid, version)];
                    }
                });
            });
        },
        getBlock: function (_a) {
            var uid = _a.uid, sid = _a.sid, version = _a.version;
            return __awaiter(this, void 0, void 0, function () {
                var block;
                return __generator(this, function (_b) {
                    switch (_b.label) {
                        case 0: return [4 /*yield*/, client(__assign({ url: "/v2/block/".concat(uid, "?").concat(query({ version: version })), apiKey: apiKey, logger: logger }, config))];
                        case 1:
                            block = (_b.sent());
                            if (block) {
                                logger.info("Block<{\"uid\":\"".concat(uid, "\",\"version\":").concat(version, "}> fetched successfully."));
                                logger.debug("\n".concat(JSON.stringify(block, null, 2)));
                            }
                            else {
                                logger.error("An error occurred while fetching Block<{\"uid\":\"".concat(uid, "\",\"version\":").concat(version, "}>, setting block state to its defaults."));
                            }
                            return [2 /*return*/, block || getDefaultBlockState(uid, sid, version)];
                    }
                });
            });
        },
        flowIntent: function (_a) {
            var sid = _a.sid, version = _a.version, intent = _a.intent;
            return __awaiter(this, void 0, void 0, function () {
                var url, response;
                return __generator(this, function (_b) {
                    switch (_b.label) {
                        case 0:
                            logger.info("Calling `".concat(intent, "` on Flow<{\"sid\":\"").concat(sid, "\",\"version\":").concat(version, "}>"));
                            url = "/v2/flow/".concat(sid, "/").concat(intent, "?").concat(query({ version: version }));
                            logger.debug("Formed url: ".concat(url));
                            return [4 /*yield*/, client(__assign({ url: url, apiKey: apiKey, logger: logger, options: INTENT_POST_OPTIONS }, config))];
                        case 1:
                            response = _b.sent();
                            if (response && response.ok) {
                                logger.info("Calling `".concat(intent, "` on Flow<{\"sid\":\"").concat(sid, "\",\"version\":").concat(version, "}> was successful."));
                                return [2 /*return*/, Promise.resolve(hasSideEffects(response))];
                            }
                            logger.error("Calling `".concat(intent, "` on Flow<{\"sid\":\"").concat(sid, "\",\"version\":").concat(version, "}> failed."));
                            return [2 /*return*/, Promise.reject()];
                    }
                });
            });
        },
        blockIntent: function (_a) {
            var uid = _a.uid, version = _a.version, transitions = _a.transitions;
            return __awaiter(this, void 0, void 0, function () {
                var transitionQueryString, url, response;
                return __generator(this, function (_b) {
                    switch (_b.label) {
                        case 0:
                            transitionQueryString = transitions
                                .map(function (transition) { return "transitions=".concat(transition); })
                                .join('&');
                            logger.info("Calling `".concat(transitionQueryString, "` on Block<{\"uid\":\"").concat(uid, "\",\"version\":").concat(version, "}>"));
                            if (transitions.length === 0) {
                                logger.error('No transitions provided, cannot transition the block');
                                return [2 /*return*/, Promise.reject()];
                            }
                            url = "/v2/block/".concat(uid, "/transition?").concat(query({
                                version: version
                            }), "&").concat(transitionQueryString);
                            logger.debug("Formed url: ".concat(url));
                            return [4 /*yield*/, client(__assign({ url: url, apiKey: apiKey, logger: logger, options: INTENT_POST_OPTIONS }, config))];
                        case 1:
                            response = _b.sent();
                            if (response && response.ok) {
                                logger.info("Calling `".concat(transitionQueryString, "` on  Block<{\"uid\":\"").concat(uid, "\",\"version\":").concat(version, "}> was successful."));
                                return [2 /*return*/, Promise.resolve(hasSideEffects(response))];
                            }
                            logger.info("Calling `".concat(transitionQueryString, "` on  Block<{\"uid\":\"").concat(uid, "\",\"version\":").concat(version, "}> failed."));
                            return [2 /*return*/, Promise.reject()];
                    }
                });
            });
        }
    };
}

function setupSocket(_a) {
    var apiKey = _a.apiKey, userId = _a.userId, log = _a.log, urlPrefix = _a.urlPrefix, groupId = _a.groupId;
    if (!userId) {
        return undefined;
    }
    var query = "/v2/client?endUserIdentifier=".concat(userId).concat(groupId ? "&groupIdentifier=".concat(groupId) : "");
    log.debug('Initializing socket connection.');
    var socket = io(urlPrefix + query, {
        path: SOCKET_PATH,
        transports: ['websocket'],
        withCredentials: true,
        auth: {
            'x-api-key': apiKey
        }
    });
    socket.on('error', function (error) {
        log.error(error);
    });
    socket.on('connect', function () {
        log.debug('Socket connected.');
    });
    socket.on('disconnect', function (reason) {
        log.debug('Socket disconnected.');
        if (reason === 'io server disconnect') {
            log.error('Disconnection was initiated by the server, something went wrong.');
        }
        else if (reason === 'io client disconnect') {
            log.debug('Disconnection initiated by the client.');
        }
        else {
            log.debug('Socket reconnecting automatically.');
        }
    });
    return socket;
}

export { blocksApi, client, getDefaultBlockState, getDefaultFlowState, queryParams, setupSocket };
